import React, { useRef } from 'react';
import { FiEdit, FiPlus, FiTrash2 } from 'react-icons/fi';
import ReactPlayer from 'react-player';
import { CircleSpinner } from 'react-spinners-kit';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { validateFileSize } from '../../../util/validator';
import { isRemoteIntroVideoPlaceholder } from '../../../util/intro-video';
import { HorizontalContent } from '../HorizontalContent';
import { RhButton, RhButtonMode } from '../RhButton';
import { RhCard } from '../RhCard';
import { colors, Description, H2 } from '../Styles';

type Props = {
  /** the current form state */
  form: { introVideo: File | undefined };
  /** called when the user edits the fields */
  userUpdatedForm: (formState: { introVideo: File | undefined }) => void;
  /** invoked when user clicks delete button */
  userClickedDelete: () => void;
  isLoadingIntroductionVideo?: boolean;
};

export const IntroductionVideoCard: React.FC<Props> = props => {
  const introVideoRef = useRef<HTMLInputElement>(null);
  const loadingIntro = !!props.isLoadingIntroductionVideo;

  return (
    <RhCard mode="tiled">
      <HorizontalContent
        left={<H2>Introduction Video</H2>}
        right={[
          <input
            key="bsp-add-intro-vid"
            type="file"
            hidden={true}
            accept="video/*"
            multiple={false}
            ref={introVideoRef}
            disabled={loadingIntro}
            onChange={e => {
              const file = e.target.files?.[0];
              if (!file) return;
              const error = validateFileSize(file);
              if (error) {
                toast.error(error);
                e.target.value = '';
                return;
              }
              props.userUpdatedForm({
                ...props.form,
                introVideo: file,
              });
            }}
          />,
          <RhButton
            key="bsp-add-intro-vid-btn"
            mode={RhButtonMode.subtle}
            prefixIcon={props.form.introVideo ? FiEdit : FiPlus}
            iconSize={18}
            disabled={loadingIntro}
            onClick={() => {
              introVideoRef.current?.click();
            }}
          />,
          props.form.introVideo && (
            <RhButton
              key="bsp-remove-intro-vid-btn"
              mode={RhButtonMode.subtle}
              prefixIcon={FiTrash2}
              iconSize={18}
              disabled={loadingIntro}
              onClick={props.userClickedDelete}
            />
          ),
        ]}
      />
      {loadingIntro ? (
        <PlayerDiv>
          <IntroLoader>
            <CircleSpinner color={colors.primary} size={40} />
            <Description>Loading introduction video…</Description>
          </IntroLoader>
        </PlayerDiv>
      ) : (
        !!props.form.introVideo && (
          <PlayerDiv>
            <ReactPlayer
              url={
                isRemoteIntroVideoPlaceholder(props.form.introVideo)
                  ? props.form.introVideo.name
                  : URL.createObjectURL(props.form.introVideo)
              }
              controls={true}
              width="100%"
              height="100%"
              style={styles.player}
            />
          </PlayerDiv>
        )
      )}
    </RhCard>
  );
};

const styles: {
  [key: string]: React.CSSProperties;
} = {
  player: {
    borderRadius: 16,
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
  },
};

const PlayerDiv = styled.div`
  width: 100%;
  margin-top: 12px;
  position: relative;
  // fixed aspect ratio box
  // see: https://github.com/cookpete/react-player#responsive-player
  padding-top: 56.25%;
`;

const IntroLoader = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 16px;
`;
